import { isString } from 'lodash';
import { getIn } from 'formik';
import Dropzone from 'react-dropzone';
import {
  Typography, FormControl, FormHelperText,
  Grid, InputLabel,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { Sortable } from '@client/components/common/Sortable';
import { DropContainer, type DropContainerProps } from './DropContainer';
import { ImagePreview } from './ImagePreview';
import { useImageUpload } from './useImageUpload.hook';
import type { PublicImageUrl } from '@sda/interfaces/image';

export const fieldToUpload = ({
  disabled,
  field: { onBlur: fieldOnBlur, ...field },
  form: { isSubmitting, touched, errors },
  onBlur,
  helperText,
  ...props
}) => {
  const fieldError = getIn(errors, field.name);
  const showError = getIn(touched, field.name) && !!fieldError;

  return {
    error: showError,
    helperText: showError ? fieldError : helperText,
    disabled: disabled ?? isSubmitting,
    ...field,
    ...props,
  };
};

const useStyles = makeStyles({ name: 'UploadBase' })((theme) => ({
  root: {
    display: 'block',
  },
  description: {
    color: theme.palette.text.disabled,
    margin: theme.spacing(1, 0),
  },
  hidden: {
    display: 'none',
  },
}));

export interface UploadBaseProps {
  readonly label?: string;
  readonly description?: string;
  readonly error?: string | boolean;
  readonly required?: boolean;
  readonly name: string;
  readonly value?: PublicImageUrl[];
  readonly disabled?: boolean;
  readonly columns?: number;
  readonly maxFiles?: number;
  readonly multiple?: boolean;
  readonly readOnly?: boolean;
  readonly onClick?: () => void;
  readonly color?: DropContainerProps['color'];
}

export const UploadBase: React.FC<UploadBaseProps> = ({
  label, description, error, required,
  name, value,
  disabled,
  columns = 3,
  maxFiles = 10,
  multiple = true,
  readOnly = false,
  onClick = () => {},
  color = 'inherit',
}) => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  const {
    handleImageRemove, handleDrop, handleImageReorder, scope,
    files, handleLaunchCropDialog,
  } = useImageUpload({ name, value, disabled });

  let columnWidth = columns === 3 ? 4 : 6;

  if (maxFiles === 1) {
    columnWidth = 12;
  } else if (isMobile && columnWidth === 4) {
    columnWidth = 6; // Force two columns for mobile
  }

  return (
    <FormControl
      className={classes.root}
      error={Boolean(error)}
      required={required}
    >
      {label && (
        <InputLabel htmlFor={name}>
          {label}
        </InputLabel>
      )}

      {description && (
        <Typography className={classes.description}>
          {description}
        </Typography>
      )}

      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        spacing={2}
      >
        <Sortable
          items={files}
          onDragEnd={handleImageReorder}
          itemComponent={(file, index) => (
            <ImagePreview
              name={name}
              key={file.path}
              disabled={disabled}
              handleImageRemove={handleImageRemove}
              handleLaunchCropDialog={handleLaunchCropDialog}
              columnWidth={columnWidth}
              index={index}
              onClick={readOnly && onClick}
              {...file}
            />
          )}
        />

        <Dropzone
          onDrop={handleDrop}
          disabled={disabled || readOnly}
          multiple={false} // Refers specifically to whether the upload can accept multiple simultaneous uploads
          accept={{
            'image/*': ['.jpeg', '.png'],
          }}
        >
          {({
            getRootProps, getInputProps, isDragActive,
          }) => (
            <DropContainer
              disabled={disabled}
              value={value}
              id={name}
              scope={scope}
              inputProps={getInputProps}
              isActive={isDragActive}
              name={name}
              rootProps={getRootProps}
              maxFiles={maxFiles}
              columnWidth={columnWidth}
              multiple={multiple}
              onClick={readOnly && onClick}
              color={color}
            />
          )}
        </Dropzone>
      </Grid>

      {isString(error) && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};
